import {
  Home,
  Image as ImageIcon,
  Users,
  User,
  Star,
  Calendar,
  Trophy,
  Type,
  Bell,
  UsersRound,
} from "lucide-react";

const APP_NAME = "Mind Match";
const APP_VERSION = "1.0.0";
export const POINTS_PER_LEVEL = 20;

/* ----------------------------------
   Helper to auto-prefix routes
----------------------------------- */
const withPrefix = <P extends string, R extends Record<string, string>>(
  prefix: P,
  routes: R,
) => {
  return Object.fromEntries(
    Object.entries(routes).map(([key, value]) => [key, `${prefix}${value}`]),
  ) as {
    [K in keyof R]: `${P}${R[K]}`;
  };
};

/* ----------------------------------
   API ROUTES (relative paths only)
----------------------------------- */
const API_ROUTES = {
  APP: {
    LOGIN: "/login",
    LOGOUT: "/logout",
    PROFILE: "/profile",
    AVATARS: "/avatars",
  },
  ADMIN: {
    LOGIN: "/login",
    LOGOUT: "/logout",
    USERS: "/users",
    AVATARS: "/avatars",
    WORDS: "/words",
    GROUPS: "/groups",
    DAILY_GAME: "/daily-game",
    SOLO_GAME: "/solo-game",
    SOLO_THREE_GAME: "/solo-three",
    ELIMINATION_GAME: "/elimination-game",
    LEADERBOARD: "/leaderboard",
    DASHBOARD: "/dashboard",
    PUSH_STATS: "/push/stats",
    PUSH_BROADCAST: "/push/broadcast",
  },
} as const;

/* ----------------------------------
   API ENDPOINTS (auto-prefixed)
----------------------------------- */
const API_ENDPOINTS = {
  APP: withPrefix("/api/app", API_ROUTES.APP),
  ADMIN: withPrefix("/api/admin", API_ROUTES.ADMIN),
  HEALTH: "/health",
} as const;

/* ----------------------------------
   HTTP STATUS CODES
----------------------------------- */
const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  NOT_FOUND: 404,
  INTERNAL_ERROR: 500,
} as const;

/* ----------------------------------
   PAGINATION CONSTANTS
----------------------------------- */
const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 10,
  MAX_LIMIT: 100,
} as const;

/* ----------------------------------
   ADMIN FRONTEND ROUTES
----------------------------------- */
const ADMIN_ROUTES = {
  DASHBOARD: {
    title: "Dashboard",
    desc: "Overview of application statistics",
    link: "/",
  },
  USERS: {
    title: "Users",
    desc: "Manage system users",
    link: "/users",
  },
  USER_DETAIL: {
    title: "User Detail",
    desc: "View user details and game history",
    link: "/users/:id",
  },
  AVATARS: {
    title: "Avatars",
    desc: "Manage system avatars",
    link: "/avatars",
  },
  WORDS: {
    title: "Word Repository",
    desc: "Manage game words",
    link: "/words",
  },
  WORD_INSIGHTS: {
    title: "Answer Insights",
    desc: "What players actually type, ranked against the saved answers",
    link: "/words/insights",
  },
  GROUPS: {
    title: "Groups",
    desc: "Review reported groups and remove abusive ones",
    link: "/groups",
  },
  DAILY: {
    title: "Daily Game",
    desc: "Manage daily games",
    link: "/daily",
  },
  SOLO: {
    title: "Solo Game",
    desc: "Manage solo games",
    link: "/solo",
  },
  SOLO_THREE: {
    title: "Solo 3 Answers",
    desc: "View Solo 3 Answers history",
    link: "/solo-three",
  },
  ELIMINATION: {
    title: "Elimination Game",
    desc: "Manage elimination games",
    link: "/elimination",
  },
  LEADERBOARD: {
    title: "Leaderboard",
    desc: "View global rankings",
    link: "/leaderboard",
  },
  NOTIFICATIONS: {
    title: "Notifications",
    desc: "Manage push notifications",
    link: "/notifications",
  },
  LOGIN: {
    title: "Login",
    desc: "Admin login",
    link: "/login",
  },
} as const;

/* ----------------------------------
   RESPONSE MESSAGES
----------------------------------- */
const RESPONSE_MESSAGES = {
  SUCCESS: {
    LOGIN: "Login successful",
    LOGOUT: "Logged out successfully",
    UPDATE: "Update successful",
    CREATE: "Created successfully",
    DELETE: "Deleted successfully",
  },
  ERROR: {
    GENERIC: "Something went wrong. Please try again.",
    SESSION_EXPIRED: "Session expired. Please login again.",
    UNAUTHORIZED: "Unauthorized access",
    NOT_FOUND: "Resource not found",
    NETWORK: "Network error. Please check your connection.",
  },
} as const;

/* ----------------------------------
   NAVIGATION ITEMS
----------------------------------- */
const NAV_ITEMS = [
  {
    title: "Dashboard",
    url: ADMIN_ROUTES.DASHBOARD.link,
    icon: Home,
  },
  {
    title: "Daily Game",
    url: ADMIN_ROUTES.DAILY.link,
    icon: Calendar,
  },
  {
    title: "Solo 3 Answers",
    url: ADMIN_ROUTES.SOLO_THREE.link,
    icon: User,
  },
  {
    title: "Elimination Game",
    url: ADMIN_ROUTES.ELIMINATION.link,
    icon: Star,
  },
  {
    title: "Leaderboard",
    url: ADMIN_ROUTES.LEADERBOARD.link,
    icon: Trophy,
  },
  {
    title: "Users",
    url: ADMIN_ROUTES.USERS.link,
    icon: Users,
  },
  {
    title: "Avatars",
    url: ADMIN_ROUTES.AVATARS.link,
    icon: ImageIcon,
  },
  {
    title: "Word Management",
    url: ADMIN_ROUTES.WORDS.link,
    icon: Type,
  },
  {
    title: "Groups",
    url: ADMIN_ROUTES.GROUPS.link,
    icon: UsersRound,
  },

  {
    title: "Notifications",
    url: ADMIN_ROUTES.NOTIFICATIONS.link,
    icon: Bell,
  },
];

/* ----------------------------------
   NOTIFICATION CONSTANTS
----------------------------------- */
const NOTIFICATION_PLATFORMS = {
  ANDROID: "android",
  IOS: "ios",
} as const;

const NOTIFICATION_ROUTES = {
  SOLO_GAME: "solo_game",
  DAILY_CHALLENGE: "daily_challenge",
  ELIMINATION_GAME: "elimination_game",
  HOME: "home",
} as const;

const NOTIFICATION_PRIORITIES = {
  NORMAL: "normal",
  HIGH: "high",
} as const;

const NOTIFICATION_CATEGORIES = {
  GAME: "game",
  CHALLENGE: "challenge",
  SYSTEM: "system",
} as const;

const NOTIFICATION_ACTIONS = {
  LIVES_RESTORED: "lives_restored",
  NEW_DAILY_GAME: "new_daily_game",
  NEW_ELIMINATION_GAME: "new_elimination_game",
  LEVEL_UP: "level_up",
  BROADCAST: "broadcast",
} as const;

// Notification Types
export type NotificationPlatform =
  (typeof NOTIFICATION_PLATFORMS)[keyof typeof NOTIFICATION_PLATFORMS];
export type NotificationRoute =
  (typeof NOTIFICATION_ROUTES)[keyof typeof NOTIFICATION_ROUTES];
export type NotificationPriority =
  (typeof NOTIFICATION_PRIORITIES)[keyof typeof NOTIFICATION_PRIORITIES];
export type NotificationCategory =
  (typeof NOTIFICATION_CATEGORIES)[keyof typeof NOTIFICATION_CATEGORIES];
export type NotificationAction =
  (typeof NOTIFICATION_ACTIONS)[keyof typeof NOTIFICATION_ACTIONS];

export {
  APP_NAME,
  APP_VERSION,
  API_ENDPOINTS,
  HTTP_STATUS,
  PAGINATION,
  ADMIN_ROUTES,
  RESPONSE_MESSAGES,
  NAV_ITEMS,
  NOTIFICATION_PLATFORMS,
  NOTIFICATION_ROUTES,
  NOTIFICATION_PRIORITIES,
  NOTIFICATION_CATEGORIES,
  NOTIFICATION_ACTIONS,
};
